import React from 'react';
import { twMerge } from 'tailwind-merge';

import Tooltip from '../../../../../../builder-io/storyblocks-design-system/components/atoms/Tooltip/Tooltip';
import { StockItemFormat } from '../../../../../../common/types/StockItemTypes';

export interface FormatButtonProps {
  format: StockItemFormat;
  formatId: string;
  isActive: boolean;
  onSelect: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onHoverStart: (formatId: string) => void;
  onHoverEnd: () => void;
  isCompact?: boolean;
  size: CardSize;
}

export type CardSize = 'small' | 'medium' | 'large';

export const FormatButton = ({
  format,
  formatId,
  isActive,
  onSelect,
  onHoverStart,
  onHoverEnd,
  size = 'large',
}: FormatButtonProps) => {
  const colorClass = isActive ? 'text-gray-900' : 'text-white';
  const baseTextClass = 'text-xs font-normal';
  const qualityText = (
    <span
      className={twMerge(
        'font-bold uppercase',
        size === 'small' && 'text-xs',
        size === 'medium' && 'text-lg',
        size === 'large' && 'text-xl',
        colorClass
      )}
    >
      {format.quality}
    </span>
  );

  const fileExtensionText = (
    <span className={twMerge('uppercase', baseTextClass, colorClass)}>
      {format.fileExtension}
    </span>
  );

  const codecText = (
    <span className={twMerge(baseTextClass, colorClass)}>
      {`(${format.codec || 'unknown'})`}
    </span>
  );

  const filesizeText = (
    <span className={twMerge(baseTextClass, colorClass)}>
      {format.filesize}
    </span>
  );

  const smallLayout = (
    <div
      data-testid="small-format-button-layout"
      className="flex items-center gap-x-1"
    >
      {qualityText}
      {fileExtensionText}
      {codecText}
    </div>
  );

  const mediumLayout = (
    <div data-testid="medium-format-button-layout" className="flex flex-col">
      <div className="flex gap-x-1 items-baseline">
        {qualityText}
        {fileExtensionText}
      </div>
      <div className="flex gap-x-1">
        {codecText}
        {filesizeText}
      </div>
    </div>
  );

  const largeLayout = (
    <div
      data-testid="large-format-button-layout"
      className="flex items-center gap-x-1"
    >
      {qualityText}
      {fileExtensionText}
      {codecText}
      {filesizeText}
    </div>
  );

  const getLayoutBySize = (size: CardSize) => {
    switch (size) {
      case 'small':
        return smallLayout;
      case 'medium':
        return mediumLayout;
      case 'large':
        return largeLayout;
    }
  };

  const button = (
    <button
      onClick={onSelect}
      className={twMerge(
        'download-triggers rounded-lg border border-white border-solid hover:bg-white hover:opacity-90',
        isActive ? 'bg-white hover:opacity-100' : '',
        size === 'large' && 'p-2',
        size === 'medium' && 'px-2',
        size === 'small' && 'px-2 py-1'
      )}
      onMouseEnter={() => onHoverStart(formatId)}
      onMouseLeave={onHoverEnd}
      title={`${format.quality} ${format.fileExtension?.toUpperCase() || ''} (${
        format.codec || 'unknown'
      }) - ${format.filesize}`}
    >
      {getLayoutBySize(size)}
    </button>
  );

  const buttonWithTooltip = (
    <Tooltip
      title={format.filesize}
      contentProps={{ side: 'right' }}
      trigger={button}
    />
  );

  return size === 'small' ? buttonWithTooltip : button;
};
